import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '@/components/AppIcon';
import EmployeeAvatar from '@/components/employees/EmployeeAvatar';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import DatePicker from '@/components/ui/DatePicker';
import Select from '@/components/ui/Select';
import LoadingState from '@/components/ui/LoadingState';
import useLockBodyScroll from '@/hooks/useLockBodyScroll';
import { useLocations } from '@/context/LocationsContext';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';

const DEFAULT_EMPLOYEE_SHAPE = {
  name: '',
  role: '',
  department: '',
  locationId: '',
  locationIds: [],
  location: '',
  locationDetails: null,
  businessId: '',
  business: null,
  phone: '',
  email: '',
  avatar: '',
  avatarUrl: '',
  avatarFileId: null,
  avatarImageBase64: '',
  avatarImageType: '',
  avatarImageName: '',
  status: 'on-work',
  isManager: false,
  employeeId: '',
  hireDate: '',
  birthday: '',
  gender: '',
  shiftPreference: '',
  address: '',
  emergencyContact: {
    name: '',
    relationship: '',
    phone: ''
  },
  documents: []
};

const EmployeeFormModal = ({
  employee,
  onClose,
  onSubmit,
  onDelete,
  canDelete = true,
  canEditAvatar = false,
  availableBranches,
  branchesLoading,
  allowRoleSelection = true,
  allowBranchSelection = true,
}) => {
  useLockBodyScroll();
  const { t } = useTranslation(['employeeDirectory', 'common']);

  const {
    locations: contextLocations,
    loading: contextLocationsLoading,
  } = useLocations();

  const locationsLoading = branchesLoading ?? contextLocationsLoading;

  const availableLocations = useMemo(() => {
    if (Array.isArray(availableBranches) && availableBranches.length > 0) {
      return availableBranches;
    }
    return contextLocations ?? [];
  }, [availableBranches, contextLocations]);

  const [formValues, setFormValues] = useState(DEFAULT_EMPLOYEE_SHAPE);
  const [isOpening, setIsOpening] = useState(true);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarUploadError, setAvatarUploadError] = useState(null);
  const isEditingExistingEmployee = Boolean(employee);
  const avatarInputRef = useRef(null);

  const branchOptions = useMemo(() => {
    const base = (availableLocations ?? [])
      .filter((loc) => loc?.id)
      .map((loc) => ({
        value: loc.id,
        label: loc?.name ?? t('employeeDirectory:form.branches.unnamed', 'Unnamed Branch'),
      }));

    if (
      formValues?.locationId
      && !base.some((option) => option?.value === formValues.locationId)
    ) {
      base.push({
        value: formValues.locationId,
        label: formValues?.location || t('employeeDirectory:form.branches.current', 'Current Branch'),
      });
    }

    return base;
  }, [availableLocations, formValues.location, formValues.locationId, t]);

  const roleOptions = useMemo(() => {
    const baseRoles = [
      'General Manager',
      'Assistant Manager',
      'Shift Lead',
      'Server',
      'Bartender',
      'Host',
      'Cook',
      'Line Cook',
      'Prep Cook',
      'Dishwasher',
    ];

    const options = baseRoles.map((role) => ({ value: role, label: role }));

    if (
      typeof formValues?.role === 'string' &&
      formValues.role.trim() &&
      !options.some((option) => option.value === formValues.role)
    ) {
      options.push({ value: formValues.role, label: formValues.role });
    }

    return options;
  }, [formValues?.role]);

  const resolveBranchById = useCallback(
    (id) => (availableLocations ?? []).find((loc) => loc?.id === id) ?? null,
    [availableLocations],
  );


  useEffect(() => {
    setIsOpening(true);
    const timeoutId = window.setTimeout(() => setIsOpening(false), 250);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [employee?.id, isEditingExistingEmployee]);

  const handleBranchSelect = useCallback((branchId) => {
    const resolved = branchId ? resolveBranchById(branchId) : null;
    setFormValues((prev) => ({
      ...prev,
      locationId: branchId ?? '',
      locationIds: branchId
        ? [
            branchId,
            ...((Array.isArray(prev?.locationIds) ? prev.locationIds : [])
              .filter((id) => id && id !== branchId)),
          ]
        : (Array.isArray(prev?.locationIds) ? prev.locationIds : []).filter(
            (id) => id && id !== prev?.locationId,
          ),
      location: resolved?.name ?? '',
      locationDetails: resolved
        ? {
            id: resolved.id,
            name: resolved.name,
            address: resolved?.address ?? '',
            active: resolved?.active ?? true,
          }
        : null,
    }));
  }, [resolveBranchById]);

  useEffect(() => {
    setAvatarUploadError(null);
    setIsUploadingAvatar(false);

    if (employee) {
      const rawHireDate = (() => {
        const candidate = employee?.raw?.hireDate ?? employee?.hireDate ?? '';
        if (!candidate) {
          return '';
        }
        const date = new Date(candidate);
        if (Number.isNaN(date.getTime())) {
          return '';
        }
        return date.toISOString().split('T')[0];
      })();

      const rawBirthday = (() => {
        const candidate = employee?.raw?.birthday ?? employee?.birthday ?? '';
        if (!candidate) {
          return '';
        }
        const date = new Date(candidate);
        if (Number.isNaN(date.getTime())) {
          return '';
        }
        return date.toISOString().split('T')[0];
      })();

      const explicitLocationId =
        employee?.locationId
        ?? employee?.locationDetails?.id
        ?? employee?.raw?.locationId
        ?? employee?.raw?.locationDetails?.id
        ?? '';

      const fallbackLocationName =
        employee?.location
        ?? employee?.locationDetails?.name
        ?? employee?.raw?.locationName
        ?? employee?.raw?.location
        ?? '';

      const matchedBranch =
        (explicitLocationId ? resolveBranchById(explicitLocationId) : null)
        ?? ((availableLocations ?? []).find((loc) => loc?.name === fallbackLocationName) ?? null);

      setFormValues({
        ...DEFAULT_EMPLOYEE_SHAPE,
        ...employee,
        locationId: matchedBranch?.id ?? (explicitLocationId || ''),
        locationIds: Array.isArray(employee?.locationIds)
          ? [...employee.locationIds]
          : Array.isArray(employee?.locations)
            ? employee.locations
                .map((assignment) => assignment?.id)
                .filter((id) => typeof id === 'string' && id.trim().length > 0)
            : [],
        location: matchedBranch?.name ?? fallbackLocationName ?? '',
        locationDetails: matchedBranch
          ? {
              id: matchedBranch?.id,
              name: matchedBranch?.name,
              address: matchedBranch?.address ?? '',
              active: matchedBranch?.active ?? true,
            }
          : employee?.locationDetails ?? null,
        hireDate: rawHireDate,
        birthday: rawBirthday,
        avatar: employee?.avatarUrl ?? employee?.avatar ?? '',
        avatarUrl: employee?.avatarUrl ?? employee?.avatar ?? '',
        avatarFileId: employee?.avatarFileId ?? employee?.raw?.avatarFileId ?? null,
        avatarImageBase64: '',
        avatarImageType: '',
        avatarImageName: '',
        emergencyContact: {
          ...DEFAULT_EMPLOYEE_SHAPE.emergencyContact,
          ...employee?.emergencyContact
        },
      });
    } else {
      setFormValues({
        ...DEFAULT_EMPLOYEE_SHAPE,
        emergencyContact: { ...DEFAULT_EMPLOYEE_SHAPE.emergencyContact }
      });
    }
  }, [employee, availableLocations, resolveBranchById]);

  useEffect(() => {
    if (!isEditingExistingEmployee && !formValues.locationId && branchOptions.length > 0) {
      handleBranchSelect(branchOptions[0]?.value);
    }
  }, [branchOptions, formValues.locationId, handleBranchSelect, isEditingExistingEmployee]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handleChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmergencyContactChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      emergencyContact: {
        ...prev.emergencyContact,
        [field]: value
      }
    }));
  };

  const handleAvatarUpload = async (event) => {
    const input = event?.target;
    const file = input?.files?.[0] ?? null;

    if (input) {
      input.value = '';
    }

    if (!file) {
      return;
    }

    setAvatarUploadError(null);

    if (!file.type?.startsWith('image/')) {
      setAvatarUploadError(
        t('employeeDirectory:form.avatar.invalidType', 'Please select an image file (PNG or JPG).'),
      );
      return;
    }

    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      setAvatarUploadError(
        t('employeeDirectory:form.avatar.fileTooLarge', 'Please select an image smaller than 5 MB.'),
      );
      return;
    }

    const readFileAsDataUrl = () =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result);
          } else {
            reject(new Error(t('employeeDirectory:form.avatar.unsupportedFormat', 'Unsupported file format.')));
          }
        };
        reader.onerror = () => reject(new Error(t('employeeDirectory:form.avatar.readError', 'Failed to read the selected file.')));
        reader.readAsDataURL(file);
      });

    setIsUploadingAvatar(true);

    try {
      const dataUrl = await readFileAsDataUrl();
      setFormValues((prev) => ({
        ...prev,
        avatar: dataUrl,
        avatarUrl: dataUrl,
        avatarImageBase64: dataUrl,
        avatarImageType: file.type ?? prev.avatarImageType ?? '',
        avatarImageName: file.name ?? prev.avatarImageName ?? '',
      }));
    } catch (error) {
      setAvatarUploadError(
        error?.message
          ?? t('employeeDirectory:form.avatar.processError', 'Failed to process the selected image. Please try again.'),
      );
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleAvatarRemove = () => {
    setAvatarUploadError(null);
    setFormValues((prev) => ({
      ...prev,
      avatar: '',
      avatarUrl: '',
      avatarFileId: null,
      avatarImageBase64: '',
      avatarImageType: '',
      avatarImageName: '',
    }));
  };

  const handleSubmit = (event) => {
    event?.preventDefault();

    const preparedEmployee = {
      ...(employee ? { ...employee } : {}),
      ...formValues,
      emergencyContact: {
        name: formValues?.emergencyContact?.name ?? '',
        relationship: formValues?.emergencyContact?.relationship ?? '',
        phone: formValues?.emergencyContact?.phone ?? ''
      },
      documents: employee?.documents ?? []
    };

    if (!['male', 'female'].includes(preparedEmployee?.gender)) {
      delete preparedEmployee.gender;
    }

    if (isEditingExistingEmployee) {
      delete preparedEmployee.gender;
    }

    if (
      typeof preparedEmployee?.avatarImageBase64 === 'string' &&
      preparedEmployee.avatarImageBase64.trim().length > 0
    ) {
      preparedEmployee.avatarImageBase64 = preparedEmployee.avatarImageBase64.trim();
      if (
        typeof preparedEmployee.avatarImageType === 'string' &&
        preparedEmployee.avatarImageType.trim().length > 0
      ) {
        preparedEmployee.avatarImageType = preparedEmployee.avatarImageType.trim();
      } else {
        delete preparedEmployee.avatarImageType;
      }
      if (
        typeof preparedEmployee.avatarImageName === 'string' &&
        preparedEmployee.avatarImageName.trim().length > 0
      ) {
        preparedEmployee.avatarImageName = preparedEmployee.avatarImageName.trim();
      } else {
        delete preparedEmployee.avatarImageName;
      }
      delete preparedEmployee.avatarFileId;
    } else {
      delete preparedEmployee.avatarImageBase64;
      delete preparedEmployee.avatarImageType;
      delete preparedEmployee.avatarImageName;

      if (Object.prototype.hasOwnProperty.call(formValues ?? {}, 'avatarFileId')) {
        const rawAvatarFileId = formValues.avatarFileId;
        const normalizedAvatarFileId =
          rawAvatarFileId === null
            ? null
            : typeof rawAvatarFileId === 'string'
              ? rawAvatarFileId.trim().length > 0
                ? rawAvatarFileId.trim()
                : undefined
              : undefined;

        const existingAvatarFileId = (() => {
          if (
            typeof employee?.avatarFileId === 'string' &&
            employee.avatarFileId.trim().length > 0
          ) {
            return employee.avatarFileId.trim();
          }
          if (
            typeof employee?.raw?.avatarFileId === 'string' &&
            employee.raw.avatarFileId.trim().length > 0
          ) {
            return employee.raw.avatarFileId.trim();
          }
          if (employee?.avatarFileId === null || employee?.raw?.avatarFileId === null) {
            return null;
          }
          return undefined;
        })();

        if (normalizedAvatarFileId === undefined) {
          delete preparedEmployee.avatarFileId;
        } else if (normalizedAvatarFileId !== existingAvatarFileId) {
          preparedEmployee.avatarFileId = normalizedAvatarFileId;
        } else {
          delete preparedEmployee.avatarFileId;
        }
      } else {
        delete preparedEmployee.avatarFileId;
      }
    }

    delete preparedEmployee.avatar;
    delete preparedEmployee.avatarUrl;

    const trimmedLocationId = typeof formValues?.locationId === 'string'
      ? formValues.locationId.trim()
      : formValues?.locationId ?? '';

    const selectedBranch = trimmedLocationId
      ? resolveBranchById(trimmedLocationId)
      : null;

    preparedEmployee.locationId = trimmedLocationId ?? '';
    preparedEmployee.location = selectedBranch?.name
      ?? formValues?.location
      ?? employee?.location
      ?? '';

    const existingLocationIds = Array.isArray(employee?.locationIds)
      ? employee.locationIds.filter((id) => typeof id === 'string' && id.trim().length > 0)
      : Array.isArray(employee?.locations)
        ? employee.locations
            .map((assignment) => assignment?.id)
            .filter((id) => typeof id === 'string' && id.trim().length > 0)
        : [];

    const formLocationIds = Array.isArray(formValues?.locationIds)
      ? formValues.locationIds
          .map((id) => (typeof id === 'string' ? id.trim() : ''))
          .filter((id) => id.length > 0 && id !== trimmedLocationId)
      : [];

    const secondaryIds = existingLocationIds.filter((id) => id !== trimmedLocationId);
    preparedEmployee.locationIds = [
      ...(trimmedLocationId ? [trimmedLocationId] : []),
      ...formLocationIds,
      ...secondaryIds,
    ].filter((value, index, array) => value && array.indexOf(value) === index);

    if (selectedBranch) {
      preparedEmployee.locationDetails = {
        id: selectedBranch?.id,
        name: selectedBranch?.name,
        address: selectedBranch?.address ?? '',
        active: selectedBranch?.active ?? true,
      };
    }

    onSubmit?.(preparedEmployee);
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-2 w-full max-w-3xl h-[85vh] overflow-hidden flex flex-col relative">
        {isOpening && (
          <div className="absolute inset-0 z-10 bg-background/70 backdrop-blur-sm">
            <LoadingState
              message={
                employee
                  ? t('employeeDirectory:form.loadingExisting', 'Loading employee data…')
                  : t('employeeDirectory:form.loadingNew', 'Preparing new employee form…')
              }
              heightClassName="h-full"
              spinnerSize="h-10 w-10"
            />
          </div>
        )}
        <div className="flex items-center justify-between p-6 border-b border-border shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {employee
                ? t('employeeDirectory:form.title.edit', 'Edit Employee')
                : t('employeeDirectory:form.title.create', 'Add Employee')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {employee
                ? t('employeeDirectory:form.subtitle.edit', 'Update employee details and status information.')
                : t('employeeDirectory:form.subtitle.create', 'Create a new employee record for your staff directory.')}
            </p>
          </div>
          <Button variant="ghost" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form
          onSubmit={handleSubmit}
          className={`flex-1 flex flex-col min-h-0 transition-opacity duration-200 ${isOpening ? 'pointer-events-none select-none opacity-0' : 'opacity-100'}`}
        >
          <div className="flex-1 overflow-y-auto p-6 space-y-6 pr-2">
            <section>
              <h3 className="text-sm font-semibold text-foreground mb-3">
                {t('employeeDirectory:form.sections.basicInfo', 'Basic Information')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={t('employeeDirectory:form.fields.fullName', 'Full Name')}
                  value={formValues?.name}
                  onChange={(e) => handleChange('name', e?.target?.value)}
                  required
                />
                <Input
                  label={t('employeeDirectory:form.fields.employeeId', 'Employee ID')}
                  value={formValues?.employeeId}
                  onChange={(e) => handleChange('employeeId', e?.target?.value)}
                  disabled={isEditingExistingEmployee}
                  className={
                    isEditingExistingEmployee
                      ? 'bg-muted text-muted-foreground'
                      : undefined
                  }
                />
                {allowRoleSelection ? (
                  <Select
                    label={t('employeeDirectory:form.fields.role', 'Role')}
                    options={roleOptions}
                    value={formValues?.role}
                    onChange={(value) => handleChange('role', value)}
                    placeholder={t('employeeDirectory:form.placeholders.role', 'Select a role')}
                    required
                  />
                ) : (
                  <div className="flex flex-col rounded-md border border-border px-3 py-2">
                    <span className="text-xs font-medium text-muted-foreground uppercase">
                      {t('employeeDirectory:form.fields.role', 'Role')}
                    </span>
                    <span className="text-sm text-foreground mt-1">{formValues?.role || '—'}</span>
                  </div>
                )}
                {allowBranchSelection ? (
                  <>
                    <Select
                      label={t('employeeDirectory:form.fields.branch', 'Branch')}
                      options={branchOptions}
                      value={formValues?.locationId}
                      onChange={handleBranchSelect}
                      required
                      loading={locationsLoading}
                      disabled={locationsLoading || branchOptions.length === 0}
                      placeholder={
                        locationsLoading
                          ? t('employeeDirectory:form.placeholders.branchesLoading', 'Loading branches…')
                          : t('employeeDirectory:form.placeholders.branch', 'Select a branch')
                      }
                    />
                    {!locationsLoading && branchOptions.length === 0 && (
                      <p className="text-xs text-muted-foreground md:col-span-2">
                        {t(
                          'employeeDirectory:form.branches.unavailable',
                          'No branches available. Please add a branch before assigning an employee.',
                        )}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col rounded-md border border-border px-3 py-2">
                    <span className="text-xs font-medium text-muted-foreground uppercase">
                      {t('employeeDirectory:form.fields.branch', 'Branch')}
                    </span>
                    <span className="text-sm text-foreground mt-1">{formValues?.location || '—'}</span>
                  </div>
                )}
                <DatePicker
                  label={t('employeeDirectory:form.fields.hireDate', 'Hire Date')}
                  value={formValues?.hireDate}
                  onChange={(e) => handleChange('hireDate', e?.target?.value)}
                />
                <DatePicker
                  label={t('employeeDirectory:form.fields.birthday', 'Birthday')}
                  value={formValues?.birthday}
                  onChange={(e) => handleChange('birthday', e?.target?.value)}
                />
                <Select
                  label={t('employeeDirectory:form.fields.gender', 'Gender')}
                  options={[
                    { label: t('employeeDirectory:form.gender.male', 'Male'), value: 'male' },
                    { label: t('employeeDirectory:form.gender.female', 'Female'), value: 'female' },
                  ]}
                  value={formValues?.gender}
                  onChange={(value) => handleChange('gender', value)}
                  placeholder={t('employeeDirectory:form.placeholders.gender', 'Select gender')}
                  disabled={isEditingExistingEmployee}
                  className={
                    isEditingExistingEmployee
                      ? 'bg-muted text-muted-foreground'
                      : undefined
                  }
                />
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-foreground mb-3">
                {t('employeeDirectory:form.sections.contactInfo', 'Contact Information')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={t('employeeDirectory:form.fields.phone', 'Phone')}
                  value={formValues?.phone}
                  onChange={(e) => handleChange('phone', e?.target?.value)}
                />
                <Input
                  label={t('employeeDirectory:form.fields.email', 'Email')}
                  type="email"
                  value={formValues?.email}
                  onChange={(e) => handleChange('email', e?.target?.value)}
                />
              </div>
              <Input
                label={t('employeeDirectory:form.fields.address', 'Address')}
                value={formValues?.address}
                onChange={(e) => handleChange('address', e?.target?.value)}
              />
            </section>

            <section>
              <h3 className="text-sm font-semibold text-foreground mb-3">
                {t('employeeDirectory:form.sections.profilePicture', 'Profile Picture')}
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <EmployeeAvatar
                    url={formValues?.avatar}
                    src={formValues?.avatar || undefined}
                    name={formValues?.name || employee?.name}
                    alt={formatTranslation(
                      t('employeeDirectory:form.avatar.alt', '{{name}} profile photo'),
                      { name: formValues?.name || employee?.name || t('employeeDirectory:card.unknownName', 'Employee') },
                    )}
                    size="lg"
                    showStatus={false}
                    wrapperClassName="border border-border"
                  />
                  <div className="flex-1 space-y-2">
                    {canEditAvatar ? (
                      <>
                        <input
                          ref={avatarInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleAvatarUpload}
                        />
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            type="button"
                            onClick={() => avatarInputRef.current?.click()}
                            disabled={isUploadingAvatar}
                          >
                            {isUploadingAvatar ? (
                              <>
                                <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                                {t('employeeDirectory:form.avatar.uploading', 'Uploading…')}
                              </>
                            ) : (
                              <>
                                <Icon name="Upload" size={16} className="mr-2" />
                                {t('employeeDirectory:form.avatar.uploadPhoto', 'Upload Photo')}
                              </>
                            )}
                          </Button>
                          {formValues?.avatar && (
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={handleAvatarRemove}
                              disabled={isUploadingAvatar}
                            >
                              {t('employeeDirectory:form.avatar.removePhoto', 'Remove Photo')}
                            </Button>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {t(
                            'employeeDirectory:form.avatar.instructions',
                            'Upload a square PNG or JPG image up to 5 MB. The profile photo link expires after a short period.',
                          )}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {t('employeeDirectory:form.avatar.restricted', 'Only the account owner can update this photo.')}
                      </p>
                    )}
                    {avatarUploadError ? (
                      <p className="text-xs text-destructive">{avatarUploadError}</p>
                    ) : null}
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-foreground mb-3">
                {t('employeeDirectory:form.sections.emergencyContact', 'Emergency Contact')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label={t('employeeDirectory:form.fields.emergencyName', 'Contact Name')}
                  value={formValues?.emergencyContact?.name}
                  onChange={(e) => handleEmergencyContactChange('name', e?.target?.value)}
                />
                <Input
                  label={t('employeeDirectory:form.fields.emergencyRelationship', 'Relationship')}
                  value={formValues?.emergencyContact?.relationship}
                  onChange={(e) => handleEmergencyContactChange('relationship', e?.target?.value)}
                />
                <Input
                  label={t('employeeDirectory:form.fields.emergencyPhone', 'Contact Phone')}
                  value={formValues?.emergencyContact?.phone}
                  onChange={(e) => handleEmergencyContactChange('phone', e?.target?.value)}
                />
              </div>
            </section>

          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-t border-dashed border-border px-6 py-4 shrink-0">
            {employee && onDelete && canDelete ? (
              <Button
                type="button"
                variant="danger"
                onClick={() => onDelete?.(employee?.id)}
                className="sm:self-start"
              >
                {t('employeeDirectory:form.actions.delete', 'Delete Employee')}
              </Button>
            ) : null}
            <div className="flex items-center justify-end space-x-3 sm:ml-auto">
              <Button variant="outline" type="button" onClick={onClose}>
                {t('employeeDirectory:form.actions.cancel', 'Cancel')}
              </Button>
              <Button type="submit">
                {employee
                  ? t('employeeDirectory:form.actions.save', 'Save Changes')
                  : t('employeeDirectory:form.actions.create', 'Create Employee')}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeFormModal;
